import { useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft } from 'lucide-react';
import { getUser } from '@/utils/auth';
import {
  LEGAL_ROLES, LAST_UPDATED, PRIVACY_POLICY, GENERAL_TERMS, ROLE_TERMS, DECLARATIONS,
} from '@/data/legalContent';

const TONES = {
  red: { bg: '#FFEBEE', border: '#EF9A9A', title: '#C62828' },
  amber: { bg: '#FFF8E7', border: '#E2A761', title: '#163329' },
  green: { bg: '#E8F5E9', border: '#81C784', title: '#2E7D32' },
  blue: { bg: '#E3F2FD', border: '#90CAF9', title: '#1565C0' },
};
const ROLE_ALIASES = { learner: 'learner', student: 'learner', user: 'learner', teacher: 'teacher', seller: 'seller' };
const SECTIONS = [
  { id: 'privacy', label: 'Privacy Policy' },
  { id: 'terms', label: 'General Terms' },
  { id: 'role-terms', label: 'Role Terms' },
  { id: 'declaration', label: 'Declaration' },
];

const ListItems = ({ items }) => (
  <ul className="list-disc pl-6 space-y-1">
    {items.map((it, i) => (
      <li key={i}>{Array.isArray(it) ? <><strong>{it[0]}:</strong> {it[1]}</> : it}</li>
    ))}
  </ul>
);

const Block = ({ block }) => {
  if (block.type === 'list') return <ListItems items={block.items} />;
  if (block.type === 'callout') {
    const t = TONES[block.tone];
    return (
      <div className="p-4 rounded-lg space-y-2" style={{ backgroundColor: t.bg, border: `1px solid ${t.border}` }}>
        {block.title && <p className="font-medium" style={{ color: t.title }}>{block.title}</p>}
        {block.text && <p>{block.text}</p>}
        {block.items && <ListItems items={block.items} />}
      </div>
    );
  }
  return <p>{block.label && <strong>{block.label}: </strong>}{block.text}</p>;
};

const Clause = ({ number, clause, role, roleLabel }) => {
  const extra = clause.byRole?.[role];
  return (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold" style={{ color: '#163329' }}>{number}. {clause.title}</h3>
      {clause.blocks.map((b, i) => <Block key={i} block={b} />)}
      {extra && (
        <div className="pl-4 border-l-4 space-y-2" style={{ borderColor: '#D86B47' }} data-testid="role-specific-addition">
          <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#D86B47' }}>For {roleLabel}s</p>
          {extra.map((b, i) => <Block key={i} block={b} />)}
        </div>
      )}
    </div>
  );
};

const Part = ({ id, title, subtitle, clauses, role, roleLabel }) => (
  <section id={id} className="mb-12 scroll-mt-32" data-testid={`legal-section-${id}`}>
    <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>{title}</h2>
    {subtitle && <p className="text-sm mb-5" style={{ color: '#8B9187' }}>{subtitle}</p>}
    <div className="space-y-8" style={{ color: '#5C635A' }}>
      {clauses.map((c, i) => <Clause key={c.title} number={i + 1} clause={c} role={role} roleLabel={roleLabel} />)}
    </div>
  </section>
);

export default function LegalPage() {
  const navigate = useNavigate();
  const { role: roleParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const section = searchParams.get('section');

  const role = useMemo(() => (
    ROLE_ALIASES[searchParams.get('role')] || ROLE_ALIASES[roleParam] || ROLE_ALIASES[getUser()?.role] || 'learner'
  ), [searchParams, roleParam]);
  const roleLabel = LEGAL_ROLES.find((r) => r.key === role).label;
  const shortLabel = roleLabel.split(' / ')[0];
  const declaration = DECLARATIONS[role];

  useEffect(() => {
    if (!section) return window.scrollTo(0, 0);
    const t = setTimeout(() => document.getElementById(section)?.scrollIntoView({ behavior: 'smooth' }), 100);
    return () => clearTimeout(t);
  }, [section]);

  const changeRole = (value) => {
    if (roleParam) return navigate(`/legal?role=${value}${section ? `&section=${section}` : ''}`, { replace: true });
    const next = new URLSearchParams(searchParams);
    next.set('role', value);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6" style={{ backgroundColor: '#FAF9F6' }} data-testid="legal-page">
      <div className="max-w-4xl mx-auto">
        <Button variant="ghost" onClick={() => navigate('/')} className="mb-6" data-testid="back-button">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Home
        </Button>

        <div className="bg-white rounded-2xl p-5 sm:p-8 shadow-sm">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
            Privacy Policy & Terms of Service
          </h1>
          <p className="text-sm mb-6" style={{ color: '#5C635A' }}>Last Updated: {LAST_UPDATED}</p>

          <p className="text-sm font-medium mb-2" style={{ color: '#163329' }}>Show terms for:</p>
          <Tabs value={role} onValueChange={changeRole} className="mb-4">
            <TabsList className="flex flex-wrap h-auto gap-1">
              {LEGAL_ROLES.map((r) => (
                <TabsTrigger key={r.key} value={r.key} className="whitespace-normal text-xs sm:text-sm" data-testid={`legal-role-tab-${r.key}`}>
                  {r.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <nav className="flex flex-wrap gap-2 mb-10 pb-6 border-b" style={{ borderColor: '#E3E1DB' }}>
            {SECTIONS.map((s) => (
              <a key={s.id} href={`#${s.id}`} className="text-xs px-3 py-1 rounded-full border hover:bg-[#F0EFEA] transition-colors" style={{ borderColor: '#E3E1DB', color: '#163329' }} data-testid={`legal-nav-${s.id}`}>
                {s.id === 'role-terms' ? `${shortLabel} Terms` : s.label}
              </a>
            ))}
          </nav>

          <Part id="privacy" title="Part 1: Privacy Policy" subtitle="Applies to all users. Items marked for your role are added below each clause." clauses={PRIVACY_POLICY} role={role} roleLabel={shortLabel} />
          <Part id="terms" title="Part 2: General Terms of Service" subtitle="Applies to all users." clauses={GENERAL_TERMS} role={role} roleLabel={shortLabel} />
          <Part id="role-terms" title={`Part 3: ${roleLabel} Terms`} subtitle={`Applies only to ${roleLabel} accounts, in addition to Parts 1 and 2.`} clauses={ROLE_TERMS[role]} role={role} roleLabel={shortLabel} />

          <section id="declaration" className="scroll-mt-32" data-testid="legal-section-declaration">
            <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>Part 4: Declaration and Acknowledgment</h2>
            <div className="p-6 rounded-lg" style={{ backgroundColor: '#F5F5F0', border: '1px solid #E3E1DB' }}>
              <p className="font-medium mb-4" style={{ color: '#163329' }}>
                By creating an account or completing a purchase on Insight Evolution, you confirm your acceptance of the following agreement:
              </p>
              <div className="p-4 rounded-lg bg-white space-y-3" style={{ border: '1px solid #163329', color: '#5C635A' }}>
                <p className="font-semibold" style={{ color: '#163329' }}>{declaration.title}</p>
                {declaration.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
